<div class="md:tw-p-6 tw-max-w-4xl tw-mx-auto">
  <!-- Header -->
  <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
    <div>
      <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900">Feature Flags</h1>
      <p class="tw-text-sm tw-text-gray-500 tw-mt-1">Manage feature flag configurations</p>
    </div>
    <button
      (click)="onAdd()"
      [disabled]="isFormOpen() || isLoading()"
      class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-shadow-sm hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
    >
      <svg class="tw-w-5 tw-h-5 tw-mr-2 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Add Feature Flag
    </button>
  </div>

  <!-- Loading State -->
  @if (isLoading()) {
  <div class="tw-flex tw-items-center tw-justify-center tw-py-12">
    <svg class="tw-animate-spin tw-h-8 tw-w-8 tw-text-blue-600" fill="none" viewBox="0 0 24 24">
      <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <span class="tw-ml-3 tw-text-gray-600">Loading feature flags...</span>
  </div>
  }

  <!-- Add/Edit Form -->
  @if (isFormOpen()) {
  <form [formGroup]="flagForm" (ngSubmit)="onSave()" class="tw-bg-white tw-rounded-lg tw-shadow-md tw-border tw-border-gray-200 tw-p-6 tw-mb-6">
    <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">
      {{ operationType() === 'add' ? 'Add New Feature Flag' : operationType() === 'edit' ? 'Edit Feature Flag' : 'Delete Feature Flag' }}
    </h2>

    <div class="tw-space-y-4">
      <!-- Default Toggle -->
      <div class="tw-flex tw-items-center tw-gap-6 tw-mb-2">
        <label for="flag-default" class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
          Default
          <span>
            <label class="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
              <input id="flag-default" type="checkbox" formControlName="default" class="tw-sr-only tw-peer" />
              <div
                class="tw-w-11 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-peer peer-focus:tw-ring-4 peer-focus:tw-ring-blue-300 peer-checked:after:tw-translate-x-full peer-checked:after:tw-border-white after:tw-content-[''] after:tw-absolute after:tw-top-0.5 after:tw-start-[2px] after:tw-bg-white after:tw-border-gray-300 after:tw-border after:tw-rounded-full after:tw-h-5 after:tw-w-5 after:tw-transition-all peer-checked:tw-bg-blue-600"
              ></div>
            </label>
          </span>
        </label>
        <label for="flag-permanent" class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
          Permanent
          <span>
            <label class="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
              <input id="flag-permanent" type="checkbox" formControlName="permanent" class="tw-sr-only tw-peer" />
              <div
                class="tw-w-11 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-peer peer-focus:tw-ring-4 peer-focus:tw-ring-blue-300 peer-checked:after:tw-translate-x-full peer-checked:after:tw-border-white after:tw-content-[''] after:tw-absolute after:tw-top-0.5 after:tw-start-[2px] after:tw-bg-white after:tw-border-gray-300 after:tw-border after:tw-rounded-full after:tw-h-5 after:tw-w-5 after:tw-transition-all peer-checked:tw-bg-blue-600"
              ></div>
            </label>
          </span>
        </label>
      </div>
      <!-- Flag Key -->
      <div>
        <label for="flag-key" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1"> Flag Key <span class="tw-text-red-500">*</span> </label>
        <input
          id="flag-key"
          type="text"
          formControlName="flag"
          placeholder="e.g., new_feature_name"
          (input)="onFlagKeyInput($event)"
          class="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 disabled:tw-bg-gray-100 disabled:tw-text-gray-500 tw-text-sm"
          [ngClass]="{
            'tw-border-red-500': flagForm.get('flag')?.invalid && flagForm.get('flag')?.touched,
            'tw-border-gray-300': !flagForm.get('flag')?.invalid || !flagForm.get('flag')?.touched
          }"
        />
        @if (flagForm.get('flag')?.invalid && flagForm.get('flag')?.touched) {
        <p class="tw-text-xs tw-text-red-500 tw-mt-1">
          @if (flagForm.get('flag')?.errors?.['required']) { Flag key is required. } @else if (flagForm.get('flag')?.errors?.['duplicate']) { A feature flag with this key already
          exists. }
        </p>
        } @else {
        <p class="tw-text-xs tw-text-gray-500 tw-mt-1">Unique identifier</p>
        }
      </div>

      <!-- Label -->
      <div>
        <label for="flag-label" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1"> Label <span class="tw-text-red-500">*</span> </label>
        <input
          id="flag-label"
          type="text"
          formControlName="label"
          placeholder="e.g., New Feature Name"
          class="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-text-sm"
          [ngClass]="{
            'tw-border-red-500': flagForm.get('label')?.invalid && flagForm.get('label')?.touched,
            'tw-border-gray-300': !flagForm.get('label')?.invalid || !flagForm.get('label')?.touched
          }"
        />
        @if (flagForm.get('label')?.invalid && flagForm.get('label')?.touched) {
        <p class="tw-text-xs tw-text-red-500 tw-mt-1">Label is required.</p>
        } @else {
        <p class="tw-text-xs tw-text-gray-500 tw-mt-1">Human-readable display name</p>
        }
      </div>

      <!-- Description -->
      <div>
        <label for="flag-description" class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1"> Description <span class="tw-text-red-500">*</span> </label>
        <textarea
          id="flag-description"
          formControlName="description"
          rows="3"
          placeholder="Describe what this feature does..."
          class="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-text-sm tw-resize-none"
          [ngClass]="{
            'tw-border-red-500': flagForm.get('description')?.invalid && flagForm.get('description')?.touched,
            'tw-border-gray-300': !flagForm.get('description')?.invalid || !flagForm.get('description')?.touched
          }"
        ></textarea>
        @if (flagForm.get('description')?.invalid && flagForm.get('description')?.touched) {
        <p class="tw-text-xs tw-text-red-500 tw-mt-1">Description is required.</p>
        }
      </div>

      <!-- Form Actions -->
      <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-pt-4 tw-border-t tw-border-gray-200">
        <button
          type="button"
          (click)="onCancel()"
          [disabled]="isSaving()"
          class="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 tw-transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="isSaving() || flagForm.invalid"
          class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-border tw-border-transparent tw-rounded-lg tw-shadow-sm hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
        >
          @if (isSaving()) {
          <svg class="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
              class="tw-opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          }
          {{ isSaving() ? 'Saving...' : operationType() === 'add' ? 'Add Flag' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </form>
  }

  <!-- Empty State -->
  @if (!isLoading() && featureFlags().length === 0 && !isFormOpen()) {
  <div class="tw-text-center tw-py-12 tw-bg-gray-50 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-gray-300">
    <svg class="tw-mx-auto tw-h-12 tw-w-12 tw-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"></path>
    </svg>
    <h3 class="tw-mt-4 tw-text-sm tw-font-medium tw-text-gray-900">No feature flags</h3>
    <p class="tw-mt-1 tw-text-sm tw-text-gray-500">Get started by creating a new feature flag.</p>
    <button
      (click)="onAdd()"
      class="tw-mt-4 tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-blue-600 tw-bg-blue-50 tw-rounded-lg hover:tw-bg-blue-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors"
    >
      <svg class="tw-w-5 tw-h-5 tw-mr-2 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Add Feature Flag
    </button>
  </div>
  }

  <!-- Feature Flags List with Virtual Scrolling -->
  @if (!isLoading() && featureFlags().length > 0) {
  <cdk-virtual-scroll-viewport itemSize="88" class="tw-h-[500px]">
    <div class="tw-space-y-3">
      @for (flag of featureFlags(); track flag.id) {
      <div class="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-p-4 hover:tw-shadow-md tw-transition-shadow">
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-justify-between">
          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
              <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">{{ flag.label }}</h3>
              <span class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-mono tw-bg-gray-100 tw-text-gray-600">
                {{ flag.flag }}
              </span>
            </div>
            <p class="tw-text-sm tw-text-gray-600 tw-line-clamp-2">{{ flag.description || 'No description provided' }}</p>
          </div>

          <div class="tw-flex sm:tw-items-center tw-gap-2 sm:tw-ms-4 sm:tw-flex-shrink-0 tw-ml-auto sm:tw-ml-0">
            <button
              (click)="onEdit(flag)"
              [disabled]="isFormOpen() || isSaving()"
              class="tw-p-2 tw-text-gray-500 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              title="Edit"
            >
              <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                ></path>
              </svg>
            </button>
            <button
              (click)="onDeleteConfirm(flag)"
              [disabled]="isFormOpen() || isSaving()"
              class="tw-p-2 tw-text-gray-500 hover:tw-text-red-600 hover:tw-bg-red-50 tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              title="Delete"
            >
              <svg class="tw-w-5 tw-h-5 tw-text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      }
    </div>
  </cdk-virtual-scroll-viewport>
  }

  <!-- Delete Confirmation Modal -->
  <modal
    [id]="deleteModalId"
    [header]="'Delete Feature Flag'"
    [showFooterDivider]="false"
    [showHeaderDivider]="false"
    [widthClass]="'tw-w-full tw-max-w-lg'"
    [heightClass]="'tw-h-auto'"
    [enableBackdropClose]="!isSaving()"
    (onCloseModal)="onCancel('delete')"
  >
    <div slot="body" class="tw-px-4 tw-pb-4 sm:tw-px-6 sm:tw-pb-4">
      <div class="sm:tw-flex sm:tw-items-start">
        <div class="tw-mx-auto tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full tw-bg-red-100 sm:tw-mx-0 sm:tw-h-10 sm:tw-w-10">
          <svg class="tw-h-6 tw-w-6 tw-text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            ></path>
          </svg>
        </div>
        <div class="tw-mt-3 tw-text-center sm:tw-mt-0 sm:tw-ml-4 sm:tw-text-left">
          <div class="tw-mt-2">
            <p class="tw-text-sm tw-text-gray-500">
              Are you sure you want to delete <span class="tw-font-semibold">{{ currentFlag()?.label }}</span
              >? This action cannot be undone.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div slot="footer" class="tw-bg-gray-50 tw-px-4 tw-py-3 sm:tw-px-6 sm:tw-flex sm:tw-flex-row-reverse tw-gap-3">
      <button
        (click)="onDelete()"
        [disabled]="isSaving()"
        class="tw-w-full tw-inline-flex tw-justify-center tw-rounded-lg tw-border tw-border-transparent tw-shadow-sm tw-px-4 tw-py-2 tw-bg-red-600 tw-text-base tw-font-medium tw-text-white hover:tw-bg-red-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-red-500 sm:tw-w-auto sm:tw-text-sm disabled:tw-opacity-50 tw-transition-colors"
      >
        @if (isSaving()) {
        <svg class="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="tw-opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        }
        {{ isSaving() ? 'Deleting...' : 'Delete' }}
      </button>
      <button
        (click)="onCancel('delete')"
        [disabled]="isSaving()"
        class="tw-mt-3 tw-w-full tw-inline-flex tw-justify-center tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-px-4 tw-py-2 tw-bg-white tw-text-base tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-gray-500 sm:tw-mt-0 sm:tw-w-auto sm:tw-text-sm disabled:tw-opacity-50 tw-transition-colors"
      >
        Cancel
      </button>
    </div>
  </modal>
</div>
